import PlayIcon from '@mui/icons-material/PlayCircleFilled';
import { IconButton, Paper, Typography } from '@mui/material';
import classNames from 'classnames';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import ThumbnailContext from '../state/context/ThumbnailContext';

const useStyles = makeStyles()((theme) => ({
  root: {
    overflow: 'hidden',
  },
  title: {
    backgroundColor: theme.palette.primary.dark,
    padding: theme.spacing(1),
  },
  embed: {
    width: '100%',
    paddingTop: '56.25%',
    position: 'relative',
  },
  content: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
  },
  iconButton: {
    width: 80,
    height: 80,
  },
  icon: {
    fontSize: 60,
  },
  image: {
    position: 'absolute !important' as any,
    width: '100%',
    '&:after': {
      content: "''",
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.3)',
      position: 'absolute',
      top: 0,
      left: 0,
    },
  },
}));

const iframe = {
  height: '100%',
  width: '100%',
  frameBorder: 0,
};

const custom = {
  youtube: {
    src: (id: string) => `https://www.youtube.com/embed/${id}`,
    parameters: {
      rel: 0,
      showinfo: 0,
      autoplay: 1,
      modestbranding: 1,
      iv_load_policy: 3,
      mute: false,
    },
    props: {
      allowFullScreen: true,
    },
  },
};

interface EmbedProps {
  className?: string;
  title?: string;
  caption?: string;
  image?: IGatsbyImageData;
  Image?: () => JSX.Element;
  youtube?: string;
  timestamp?: number;
  muted?: boolean;
}

/**
 * Image: a StaticImage component
 * image: must be of type gatsbyImageData
 */
function Embed({
  className,
  title = '',
  caption = '',
  image,
  Image,
  youtube = '',
  timestamp = 0,
  muted = true,
}: EmbedProps) {
  const { classes } = useStyles();

  const [active, setActive] = React.useState(false);

  let customProps;
  if (youtube) {
    if (muted !== undefined) {
      custom.youtube.parameters.mute = muted;
    }
    const youtubeParameters = {
      ...custom.youtube.parameters,
      ...(timestamp && { start: timestamp }),
    };

    customProps = {
      src:
        custom.youtube.src(youtube) +
        (youtubeParameters
          ? `?${Object.entries(youtubeParameters)
              .map((obj) => [...obj].map(encodeURIComponent).join('='))
              .join('&')}`
          : ''),
      ...custom.youtube.props,
    };
  }

  const iframeProps = { ...iframe, ...customProps };

  let PreviewImage: React.ReactElement | null = null;
  if (Image) {
    PreviewImage = (
      <div className={classes.image}>
        <Image />
      </div>
    );
  } else {
    PreviewImage = (
      <ThumbnailContext.Consumer>
        {(contextImage) => {
          const img = image || contextImage;
          if (!img) return null;
          return (
            <div className={classes.image}>
              <GatsbyImage image={img} alt="" />
            </div>
          );
        }}
      </ThumbnailContext.Consumer>
    );
  }

  return (
    <div>
      {title && (
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
      )}
      <div className={classNames(classes.root, className)}>
        <Paper>
          <div className={classes.embed}>
            <div className={classes.content}>
              {active ? (
                <iframe title="Embed" {...iframeProps} />
              ) : (
                <>
                  {PreviewImage}

                  <IconButton
                    className={classes.iconButton}
                    aria-label="Play"
                    onClick={() => setActive(true)}
                    size="large"
                  >
                    <PlayIcon className={classes.icon} />
                  </IconButton>
                </>
              )}
            </div>
          </div>
        </Paper>
      </div>
      <Typography variant="caption">{caption}</Typography>
    </div>
  );
}

export default Embed;
